import React from 'react'
import styled from 'styled-components'
import { onBackClickCallback, onNextClickType } from 'components/types'
import { Menopause, Menoscale, Quizzes } from '../../redux/quiz/types'
import { DSButton, DSFlex, DSModal, DSSpacer, DSText } from '@zoe/ds-web'
import {
  calculateMenoscaleResults,
  getSymptomLabel,
  MenopauseAgeRange,
  MenopauseAverageData,
} from '../../libs/menoscale/utils'
import { LoadingSpinner } from '../core/spinner/LoadingSpinner'
import { MenoscaleScoreBar } from '../core/bars/MenoscaleScoreBar'
import { BigFeatureText } from '../core/text/BigFeatureText'
import { BackgroundCard } from '../core/cards/BackgroundCard'
import { ProgressBar } from '../screen-components/ProgressBar'
import { useSelector } from 'react-redux'
import { selectQuizCountry } from '../../redux/quiz/slice'
import { trackQuizUserClick } from 'components/gtmV2'
import { useFocusOnMount } from '../../hooks/useFocusOnMount'

const ResponsiveWidth = styled.div`
  width: 100%;
  @media (min-width: 40em) {
    width: 60%;
  }
`

const COLOURS = {
  backgroundPrimary: 'rgba(83, 45, 60, 1)',
  primary: '#FFD100',
  backgroundSecondary: '#F1F7F6',
}

export type MenoScaleResultsScreenProps = {
  title: string
  description: string
}

type Props = MenoScaleResultsScreenProps & {
  onNextClick: onNextClickType
  onBackClick: onBackClickCallback
  quiz: Quizzes
}

type Symptom = {
  name: string
  averageScore: number
  score: number
}
const LegendItem = ({ color, text }: { color: string; text: string }) => (
  <>
    <div style={{ borderRadius: '50%', width: '12px', height: '12px', background: color }} />
    <DSSpacer size={4} direction="horizontal" />
    <DSText variant="fluid-label-100" weight="light">
      {text}
    </DSText>
  </>
)
const resultMessage = (score: number, menopauseAverageData: MenopauseAverageData | null) => {
  if (menopauseAverageData) {
    const averages = Object.values(menopauseAverageData).filter((average) => average !== null) as number[]
    const lowestAverage = Math.min(...averages)
    const highestAverage = Math.max(...averages)

    // we compute the greatest difference between the user's score and the average
    const diff = Math.max(Math.abs(score - lowestAverage), Math.abs(score - highestAverage))
    console.log({ lowestAverage, highestAverage, averages, diff })
    if (diff < 10) {
      return 'Your score is about average'
    } else if (score < lowestAverage) {
      return 'Your score is lower than average'
    } else if (score > highestAverage) {
      return 'Your score is higher than average'
    } else if (score > lowestAverage && score < highestAverage) {
      return 'Your score is between averages'
    }
  } else {
    return 'Your MenoScale score'
  }
}

const calculatePostScoreMessage = (
  averages: MenopauseAverageData,
  ageRange: MenopauseAgeRange,
  menopause: Menopause,
) => {
  switch (menopause) {
    case 'perimenopausal':
    case 'postmenopausal':
      return `Other ${menopause} women aged ${ageRange} have an average score of ${averages[menopause]}`
    case 'premenopausal':
    case 'do_not_know':
    case 'prefer_not_to_say':
      const perimenopausalMessage = averages.perimenopausal ? `${averages.perimenopausal} if perimenopausal` : null
      const postMenopausalMessage = averages.postmenopausal ? `${averages.postmenopausal} if postmenopausal` : null
      const message = [perimenopausalMessage, postMenopausalMessage].filter((message) => !!message).join(', and ')
      return `Other women aged ${ageRange} had an average score of ${message}`
  }
}

const ResultsCard = ({
  loading,
  score,
  averages,
  ageRange,
  menopause,
}: {
  loading: boolean
  score: number
  averages: MenopauseAverageData | null
  ageRange: MenopauseAgeRange | null
  menopause: Menopause
}) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false)

  return (
    <BackgroundCard backgroundColor={COLOURS.backgroundPrimary}>
      <DSText variant="fluid-paragraph-200" weight="semiBold" color="secondary-inverse">
        Your MenoScale score
      </DSText>
      {loading && <LoadingSpinner color={COLOURS.primary} />}
      {!loading && (
        <>
          <DSSpacer size={8} direction="vertical" />
          <BigFeatureText color={COLOURS.primary}>{score}</BigFeatureText>
        </>
      )}
      <DSSpacer size={24} direction="vertical" />
      <DSText variant="fluid-heading-600" weight="semiBold" color="secondary-inverse">
        {loading ? 'Calculating...' : resultMessage(score, averages)}
      </DSText>
      {!loading && (
        <>
          <DSSpacer size={24} direction="vertical" />
          <MenoscaleScoreBar score={score} averages={averages} menopauseStatus={menopause} />
        </>
      )}
      {!loading && averages && ageRange && (
        <>
          <DSSpacer size={24} direction="vertical" />
          <ResponsiveWidth>
            <DSSpacer size={24} direction="vertical" />
            <DSText variant="fluid-paragraph-200" weight="light" color="secondary-inverse">
              {calculatePostScoreMessage(averages, ageRange, menopause)}
            </DSText>
          </ResponsiveWidth>
        </>
      )}
      {!loading && (
        <>
          <DSSpacer size={24} direction="vertical" />
          <a
            href=""
            onClick={(evt) => {
              evt.preventDefault()
              setIsModalOpen(true)
            }}
          >
            <DSFlex direction="row" alignItems="center">
              <DSText variant="fluid-paragraph-200" weight="semiBold" color="secondary-inverse">
                How is my score calculated?
              </DSText>
              <DSSpacer size={4} direction="horizontal" />

              {/* todo - use DSIcon when fixed */}
              <svg width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M7.00024 7.87862V7.29641C7.00024 7.23295 7.04702 7.1792 7.10965 7.169C7.28925 7.13972 7.64433 7.07256 7.82183 6.97519C8.33797 6.69156 8.68774 6.15017 8.68774 5.52818C8.68774 4.61264 7.93242 3.87081 7.00024 3.87081C6.06806 3.87081 5.31274 4.61264 5.31274 5.52818M7 12.125C10.1066 12.125 12.625 9.6066 12.625 6.5C12.625 3.3934 10.1066 0.875 7 0.875C3.8934 0.875 1.375 3.3934 1.375 6.5C1.375 9.6066 3.8934 12.125 7 12.125Z"
                  stroke="white"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7.0008 8.76379C7.3236 8.76379 7.58558 9.0254 7.58558 9.34858C7.58558 9.67175 7.3236 9.93336 7.0008 9.93336C6.67761 9.93336 6.41602 9.67175 6.41602 9.34858C6.41602 9.0254 6.67761 8.76379 7.0008 8.76379Z"
                  fill="white"
                />
              </svg>
            </DSFlex>
          </a>
          <DSModal surfaceColor="neutral" isOpen={isModalOpen} onCloseButtonClick={() => setIsModalOpen(false)}>
            <div style={{ maxWidth: '90%' }}>
              <DSText variant="fluid-heading-600" weight="semiBold">
                How is my score calculated?
              </DSText>
            </div>
            <DSSpacer size={8} direction="vertical" />
            <DSText variant="fluid-paragraph-200" weight="light">
              The MenoScale asks 20 questions about common symptoms of menopause. For each symptom, you will rate how
              much it impacts your life: (0=not at all, 1=a little, 3=quite a bit and 5=extremely). This provides a
              total score out of 100.
            </DSText>
            <DSSpacer size={8} direction="vertical" />
          </DSModal>
        </>
      )}
    </BackgroundCard>
  )
}

const MainSymptomsCard = ({ symptoms, country }: { symptoms: Symptom[]; country?: string }) => (
  <BackgroundCard padding="32px">
    <DSSpacer size={12} direction="vertical" />

    <div style={{ textAlign: 'center' }}>
      <DSText variant="fluid-heading-600">You&rsquo;re not alone</DSText>
      <DSSpacer size={12} direction="vertical" />
      <DSText variant="fluid-paragraph-300" weight="light">
        Here&apos;s how you compare to women your age
      </DSText>
    </div>
    <DSSpacer size={32} direction="vertical" />

    <div
      style={{
        backgroundColor: '#F1F7F6',
        padding: '24px 16px',
        width: '100%',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div style={{ width: '100%', textAlign: 'left' }}>
        <DSText variant="fluid-paragraph-200" weight="semiBold">
          Your 3 main symptoms
        </DSText>
      </div>

      <div style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center' }}>
        <LegendItem color={COLOURS.primary} text="You" />
        <DSSpacer size={24} direction="horizontal" />
        <LegendItem color={COLOURS.backgroundPrimary} text="Average for similar women" />
      </div>

      {symptoms.map((symptom) => (
        <div key={symptom.name} style={{ width: '100%', textAlign: 'left' }}>
          <DSSpacer size={24} direction="vertical" />
          <DSText variant="fluid-heading-500" weight="semiBold">
            {getSymptomLabel(symptom.name, country)}
          </DSText>
          <DSSpacer size={16} direction="vertical" />
          <ProgressBar
            size="medium"
            completionPercentage={symptom.score / 5}
            color={COLOURS.primary}
            backgroundColor="hsla(229, 15%, 15%, 0.05)"
          />
          <DSSpacer size={8} direction="vertical" />
          <ProgressBar
            size="medium"
            completionPercentage={symptom.averageScore / 5}
            color={COLOURS.backgroundPrimary}
            backgroundColor="hsla(229, 15%, 15%, 0.05)"
          />
          <DSSpacer size={24} direction="vertical" />
        </div>
      ))}
      <div style={{ width: '100%', textAlign: 'left', display: 'none', alignItems: 'center' }}>
        <LegendItem color={COLOURS.primary} text="You" />
        <DSSpacer size={24} direction="horizontal" />
        <LegendItem color={COLOURS.backgroundPrimary} text="Average for similar women" />
      </div>
    </div>
  </BackgroundCard>
)

const WhatsNextSection = () => {
  const breakConfig = {
    maxViewportDirection: 'row',
    breakAt: {
      breakPoint: 'm',
      direction: 'column',
    },
  }
  return (
    <BackgroundCard backgroundColor="#faf8f5" padding="0">
      <DSFlex
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        // design system is not recognizing the breakConfig type
        breakConfig={breakConfig as any}
        width="100%"
      >
        <div
          style={{
            backgroundColor: '#faf8f5',
            width: '100%',
            maxWidth: '600px',
            overflow: 'hidden',
            borderRadius: '24px',
          }}
        >
          <img
            loading="lazy"
            src="/img/menoscale/menopause-nutrition-guide.png"
            alt="Menopause nutrition guide"
            style={{ display: 'block', margin: '32px auto 0', width: '100%', maxHeight: '600px', objectFit: 'contain' }}
          />
        </div>
        <DSSpacer size={24} direction="horizontal" />
        <div style={{ width: '100%', textAlign: 'left', padding: '32px' }}>
          <DSSpacer size={24} direction="vertical" />
          <DSText variant="fluid-heading-600" weight="semiBold">
            What&rsquo;s next?
          </DSText>
          <DSSpacer size={12} direction="vertical" />
          <DSText variant="fluid-paragraph-300" weight="light">
            Thrive at every stage of life.
          </DSText>
          <DSText variant="fluid-paragraph-300" weight="light">
            Look out for our free Menopause Nutrition Guide in your inbox.
          </DSText>
        </div>
      </DSFlex>
    </BackgroundCard>
  )
}
const CTASection = () => {
  const breakConfig = {
    maxViewportDirection: 'row',
    breakAt: {
      breakPoint: 'm',
      direction: 'column-reverse',
    },
  }

  const handleCTAClick = () => {
    trackQuizUserClick('menoscale', undefined, 'cta', { pageName: 'menoscale_results' })
    window.location.href = 'https://zoe.com/quiz'
  }

  return (
    <BackgroundCard backgroundColor="#fff" padding="16px">
      <DSFlex
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        // design system is not recognizing the breakConfig type
        breakConfig={breakConfig as any}
        width="100%"
      >
        <div style={{ width: '100%', padding: '32px', textAlign: 'left' }}>
          <DSSpacer size={24} direction="vertical" />
          <DSText variant="fluid-heading-600" weight="semiBold">
            Curious for more?
          </DSText>
          <DSSpacer size={12} direction="vertical" />
          <DSText variant="fluid-paragraph-300" weight="light">
            ZOE Membership is a personalized nutrition program that helps you make smarter food choices.
          </DSText>
          <DSSpacer size={24} direction="vertical" />
          <DSButton colorScheme="default" onPress={handleCTAClick} size="large" variant="secondary" width="auto">
            Become a ZOE member
          </DSButton>
        </div>
        <div style={{ width: '100%', overflow: 'hidden', borderRadius: '24px' }}>
          <img
            loading="lazy"
            src="/img/menoscale/women-eating-outdoors.png"
            alt="Women eating outdoors"
            style={{
              borderRadius: '1rem',
              boxShadow: 'rgba(0, 0, 0, 0.05) 0px 0px 20px 0px',
              width: '100%',
              height: 'auto',
              objectFit: 'cover',
              maxHeight: '400px',
            }}
          />
        </div>
      </DSFlex>
    </BackgroundCard>
  )
}

export const MenoScaleResultsScreen: React.FC<Props> = (props: Props) => {
  const menoscale = props.quiz as Menoscale
  const [loading, setLoading] = React.useState(true)
  const results = calculateMenoscaleResults(menoscale)
  const quizCountry = useSelector(selectQuizCountry)
  const mainRef = useFocusOnMount<HTMLElement>()
  React.useEffect(() => {
    setTimeout(() => {
      setLoading(false)
    }, 3500)
  }, [])

  return (
    <main
      ref={mainRef}
      tabIndex={-1}
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        outline: 'none',
      }}
    >
      <ResultsCard
        loading={loading}
        score={results.score}
        averages={results.averages}
        menopause={results.menopauseStatus ?? 'do_not_know'}
        ageRange={results.ageRange}
      />
      <DSSpacer size={72} direction="vertical" />
      {!loading && results.mainSymptoms.length > 0 && (
        <MainSymptomsCard symptoms={results.mainSymptoms} country={quizCountry} />
      )}
      <DSSpacer size={72} direction="vertical" />
      {!loading && <WhatsNextSection />}
      <DSSpacer size={72} direction="vertical" />
      {!loading && <CTASection />}
      <DSSpacer size={72} direction="vertical" />
    </main>
  )
}
